/**
 * Prompt-injection input sanitization.
 *
 * Customers don't write prompts directly — but lead headlines, custom
 * voice samples, ICP descriptions, and offer/pain-point fields all flow
 * unchanged into the AI prompt context. A malicious lead headline could
 * try to overwrite system instructions ("Ignore previous instructions
 * and …"). Our LLM has no tool access, so the worst outcome is a weird
 * outbound message — but stripping prompt-control tokens is cheap
 * hygiene and explicitly called out in DCA-Security-Posture.pdf.
 *
 * Strips:
 *   - Anthropic / OpenAI control sequences: <|...|>, <|im_start|>, etc.
 *   - Common system-prompt headers: "### Instruction:", "### System:"
 *   - "[INST] ... [/INST]" wrappers (Llama-style)
 *   - "Ignore previous instructions" jailbreak phrases
 *   - Excessive newlines that fragment context
 *   - Length cap at 2000 chars (truncates with ellipsis)
 */

const CONTROL_TOKEN_PATTERNS: readonly RegExp[] = [
  /<\|[^|]{0,40}\|>/g,                    // <|im_start|>, <|endoftext|>, etc.
  /\[\/?INST\]/gi,                         // [INST] [/INST]
  /<\/?(?:s|system|user|assistant)>/gi,    // <s>, </system>, etc.
  /^[ \t]*###[ \t]*(?:instruction|system|prompt|response|user|assistant)[: ]*/gim,
  /^[ \t]*(?:system|user|assistant)[ \t]*:[ \t]*$/gim,
] as const

const JAILBREAK_PHRASE_PATTERNS: readonly RegExp[] = [
  /ignore\s+(?:all\s+)?(?:previous|prior|above|earlier)\s+(?:instructions?|prompts?|messages?)/gi,
  /disregard\s+(?:all\s+)?(?:previous|prior|above|earlier)\s+(?:instructions?|prompts?)/gi,
  /forget\s+(?:everything|all|the\s+above|previous\s+instructions?)/gi,
  /you\s+are\s+(?:now\s+)?(?:a\s+)?(?:dan|developer\s+mode|jailbroken|unrestricted|uncensored)/gi,
  /pretend\s+(?:you\s+(?:are|have))\s+(?:no\s+restrictions|no\s+rules|no\s+limits)/gi,
] as const

/**
 * Sanitize a free-text user-controlled string before it enters an AI
 * prompt. Idempotent and order-independent: running twice gives the
 * same result.
 */
export function sanitizeUserPromptInput(
  input: string | null | undefined,
  options: { readonly maxLength?: number } = {},
): string {
  if (!input) return ''
  const maxLength = options.maxLength ?? 2000

  let s = input

  // Strip control tokens
  for (const re of CONTROL_TOKEN_PATTERNS) {
    s = s.replace(re, ' ')
  }

  // Strip jailbreak phrases (replace with neutral filler so paragraph
  // structure isn't destroyed, but the imperative is removed).
  for (const re of JAILBREAK_PHRASE_PATTERNS) {
    s = s.replace(re, '[redacted]')
  }

  // Collapse runs of newlines (>3) — these are sometimes used to push
  // earlier context out of focus.
  s = s.replace(/\n{4,}/g, '\n\n\n')

  // Trim excessive whitespace
  s = s.replace(/[ \t]+/g, ' ').trim()

  // Cap length (truncate with ellipsis so the model knows it was cut)
  if (s.length > maxLength) {
    s = s.slice(0, maxLength - 3) + '...'
  }

  return s
}

/**
 * Sanitize all string fields of a record (shallow). Useful when a whole
 * lead-profile object flows into the prompt builder.
 */
export function sanitizeRecordStrings<T extends Record<string, unknown>>(
  record: T,
  options: { readonly maxLength?: number } = {},
): T {
  const out: Record<string, unknown> = { ...record }
  for (const [k, v] of Object.entries(record)) {
    if (typeof v === 'string') {
      out[k] = sanitizeUserPromptInput(v, options)
    }
  }
  return out as T
}
